import React, { useState, useEffect, useCallback, useMemo } from "react";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutlineOutlined";

import PageContainer from "../layouts/PageContainer";
import CardGridContainer from "../layouts/CardGridContainer";
import PageToolbar from "../components/common/PageToolbar";
import AppButton from "../components/buttons/AppButton";
import RoleCard from "../components/roles/RoleCard";
import CreateRoleModal from "../components/roles/CreateRoleModal";
import UpdateRoleModal from "../components/roles/UpdateRoleModal";
import ConfirmDialog from "../components/common/ConfirmDialog";

import { usePermissions } from "../hooks/usePermissions";
import { useAppDispatch, useAppSelector } from "../redux/store";
import { getRoles, deleteRole } from "../redux/actions/roleActions";
import { roleActions } from "../redux/slices/role";
import { RoleResponseDto } from "../shared/dtos/role/RoleResponseDto";

const RolesAndPermissionsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { hasPermission } = usePermissions();

  const {
    items: roles,
    loadingFetchAll: loadingRoles,
    errorFetchAll: errorLoadingRoles,
    serverStatus,
    loadingDelete: loadingRoleDelete,
    loadingUpdate: loadingRoleUpdate,
    loadingCreate: loadingRoleCreate,
    errorCreate: errorCreatingRole,
    errorUpdate: errorUpdatingRole,
  } = useAppSelector((state) => state.role);

  // Local Search State
  const [searchTerm, setSearchTerm] = useState<string>("");

  // Modal State
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState<RoleResponseDto | null>(
    null,
  );

  // Permissions
  const canCreateRole = hasPermission("role:create");
  const canUpdateRole = hasPermission("role:update");
  const canDeleteRole = hasPermission("role:delete");

  const fetchRoles = useCallback((): void => {
    dispatch(getRoles());
  }, [dispatch]);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  // Pure frontend in-memory filtering
  const filteredRoles = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return roles;

    return roles.filter((role) => {
      const matchName = role.name?.toLowerCase().includes(q);
      const matchDesc = (role as any).description?.toLowerCase().includes(q);
      return Boolean(matchName || matchDesc);
    });
  }, [roles, searchTerm]);

  // Modal Handlers
  const handleOpenCreate = (): void => setIsCreateModalOpen(true);
  const handleCloseCreate = useCallback((): void => {
    if (!loadingRoleCreate) setIsCreateModalOpen(false);
  }, [loadingRoleCreate]);

  const handleOpenUpdate = (role: RoleResponseDto): void => {
    setSelectedRole(role);
    setIsUpdateModalOpen(true);
  };
  const handleCloseUpdate = useCallback((): void => {
    if (!loadingRoleUpdate) {
      setIsUpdateModalOpen(false);
      setSelectedRole(null);
    }
  }, [loadingRoleUpdate]);

  const handleOpenDelete = (role: RoleResponseDto): void => {
    setSelectedRole(role);
    setIsDeleteModalOpen(true);
  };
  const handleCloseDelete = useCallback((): void => {
    if (!loadingRoleDelete) {
      setIsDeleteModalOpen(false);
      setSelectedRole(null);
    }
  }, [loadingRoleDelete]);

  const handleDeleteConfirm = (): void => {
    if (selectedRole) {
      dispatch(deleteRole(selectedRole.id));
    }
  };

  // Auto-close modals on success
  useEffect(() => {
    if (!serverStatus) return;
    const { operation, status } = serverStatus;

    if (operation === "create" && status === 201) handleCloseCreate();
    if (operation === "update" && status === 200) handleCloseUpdate();
    if (operation === "delete" && status === 200) handleCloseDelete();

    dispatch(roleActions.setServerStatus(null));
  }, [
    serverStatus,
    dispatch,
    handleCloseCreate,
    handleCloseUpdate,
    handleCloseDelete,
  ]);

  return (
    <PageContainer
      title="Roles & Permissions"
      subtitle="Define user roles and assign specific permissions for system access."
      actions={
        canCreateRole && (
          <AppButton
            variant="contained"
            startIcon={<AddCircleOutlineIcon />}
            onClick={handleOpenCreate}
          >
            Create New Role
          </AppButton>
        )
      }
    >
      <PageToolbar
        searchTerm={searchTerm}
        onSearchChange={(e) => setSearchTerm(e.target.value)}
        searchPlaceholder="Search roles by name or description..."
      />

      <CardGridContainer
        loading={loadingRoles}
        error={errorLoadingRoles}
        itemCount={filteredRoles.length}
        emptyMessage="No Roles Found"
        onRetry={fetchRoles}
      >
        {filteredRoles.map((role) => (
          <RoleCard
            key={role.id}
            role={role}
            onEdit={canUpdateRole ? () => handleOpenUpdate(role) : undefined}
            onDelete={canDeleteRole ? () => handleOpenDelete(role) : undefined}
          />
        ))}
      </CardGridContainer>

      {/* CREATE MODAL */}
      {isCreateModalOpen && (
        <CreateRoleModal
          open={isCreateModalOpen}
          onClose={handleCloseCreate}
          loading={loadingRoleCreate}
          error={errorCreatingRole}
        />
      )}

      {/* UPDATE MODAL */}
      {isUpdateModalOpen && selectedRole && (
        <UpdateRoleModal
          open={isUpdateModalOpen}
          onClose={handleCloseUpdate}
          role={selectedRole}
          loading={loadingRoleUpdate}
          error={errorUpdatingRole}
        />
      )}

      {/* DELETE CONFIRM DIALOG */}
      {isDeleteModalOpen && selectedRole && (
        <ConfirmDialog
          open={isDeleteModalOpen}
          onClose={handleCloseDelete}
          onConfirm={handleDeleteConfirm}
          title="Delete Role"
          description={`Are you sure you want to delete the role "${selectedRole.name}"? This action cannot be undone.`}
          loading={loadingRoleDelete}
        />
      )}
    </PageContainer>
  );
};

export default RolesAndPermissionsPage;
